import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'urql'
import { usePhotoUpload } from '../context/PhotoUploadProvider'
import { GetOrderPhotos } from '../graphql/orderPhotos'
import { entriesForOrder } from '../helpers/orderPhotoQueueView'
import { readQueuedPhotoBlobs } from '../helpers/photoQueueStorage'

export type OrderPhotoView = {
  clientId: string
  thumbSrc: string
  fullSrc: string
  pending: boolean
  failed: boolean
  uploadedByName?: string | null
  takenAt?: string | null
  createdAt?: string | null
}

/**
 * Server photos ∪ locally queued photos for one order, in one gallery-ready
 * list. Queued entries are deduped by clientId (a server photo with the same
 * clientId wins — the upload landed) and rendered from on-device object URLs.
 */
export const useOrderPhotos = (orderId: number, opts?: { pause?: boolean }) => {
  const [result, reexecute] = useQuery({
    query: GetOrderPhotos,
    variables: { orderId },
    // Presigned URLs expire (~1h): always revalidate, render cache meanwhile
    requestPolicy: 'cache-and-network',
    pause: opts?.pause,
  })
  const { pendingPhotos, recentlyUploaded, uploadSerial, confirmUploaded } =
    usePhotoUpload()

  const serverPhotos = result.data?.order?.photos ?? []
  const queuedForOrder = entriesForOrder(pendingPhotos, orderId, serverPhotos)
  // Uploaded by the drain but not yet in the (stale) server list — keep them
  // on screen so the photo never blinks out while the refetch is in flight.
  const uploadedForOrder = entriesForOrder(
    recentlyUploaded,
    orderId,
    serverPhotos,
  )

  // A drain finished uploading something: revalidate the server list.
  const lastUploadSerial = useRef(uploadSerial)
  useEffect(() => {
    if (uploadSerial === lastUploadSerial.current) return
    lastUploadSerial.current = uploadSerial
    reexecute({ requestPolicy: 'network-only' })
  }, [uploadSerial, reexecute])

  // Server list caught up: release the retained local copies.
  const serverKey = serverPhotos.map((photo) => photo.clientId).join(',')
  useEffect(() => {
    const serverIds = new Set(serverKey ? serverKey.split(',') : [])
    const confirmed = recentlyUploaded
      .filter((entry) => serverIds.has(entry.clientId))
      .map((entry) => entry.clientId)
    if (confirmed.length > 0) confirmUploaded(confirmed)
  }, [serverKey, recentlyUploaded, confirmUploaded])

  // Local previews: one object URL per queued photo, revoked when it leaves
  // the queue (or on unmount). URLs are minted and revoked OUTSIDE setState
  // updaters — updaters must stay pure (StrictMode re-invokes them).
  type LocalSrcs = { thumb: string; full: string }
  const [localSrcs, setLocalSrcs] = useState<Record<string, LocalSrcs>>({})
  const localSrcsRef = useRef(localSrcs)
  localSrcsRef.current = localSrcs

  // Previews live for queued AND uploaded-awaiting entries (the uploaded
  // ones can't be re-read from disk, but their object URL is still alive).
  const localKey = [...queuedForOrder, ...uploadedForOrder]
    .map((entry) => entry.clientId)
    .join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: localKey tracks the local set
  useEffect(() => {
    let cancelled = false
    const localIds = new Set(localKey ? localKey.split(',') : [])

    const load = async () => {
      for (const entry of queuedForOrder) {
        if (localSrcsRef.current[entry.clientId]) continue
        try {
          // Both renditions: the grid must draw the 400px thumb, not decode
          // a 2560px bitmap per tile.
          const { photo, thumb } = await readQueuedPhotoBlobs(
            entry.clientId,
            entry.contentType,
          )
          const srcs: LocalSrcs = {
            thumb: URL.createObjectURL(thumb),
            full: URL.createObjectURL(photo),
          }
          if (cancelled) {
            URL.revokeObjectURL(srcs.thumb)
            URL.revokeObjectURL(srcs.full)
            return
          }
          setLocalSrcs((current) =>
            current[entry.clientId]
              ? current
              : { ...current, [entry.clientId]: srcs },
          )
        } catch (error) {
          console.error(
            '[useOrderPhotos] preview unavailable',
            entry.clientId,
            error,
          )
        }
      }
    }
    void load()

    // Revoke previews for photos that no longer need a local copy
    const stale = Object.entries(localSrcsRef.current).filter(
      ([clientId]) => !localIds.has(clientId),
    )
    if (stale.length > 0) {
      for (const [, srcs] of stale) {
        URL.revokeObjectURL(srcs.thumb)
        URL.revokeObjectURL(srcs.full)
      }
      const staleIds = new Set(stale.map(([clientId]) => clientId))
      setLocalSrcs((current) =>
        Object.fromEntries(
          Object.entries(current).filter(
            ([clientId]) => !staleIds.has(clientId),
          ),
        ),
      )
    }

    return () => {
      cancelled = true
    }
  }, [localKey])

  useEffect(
    () => () => {
      for (const srcs of Object.values(localSrcsRef.current)) {
        URL.revokeObjectURL(srcs.thumb)
        URL.revokeObjectURL(srcs.full)
      }
    },
    [],
  )

  const photos: OrderPhotoView[] = [
    ...serverPhotos.map((photo) => ({
      clientId: photo.clientId,
      thumbSrc: photo.thumbUrl,
      fullSrc: photo.url,
      pending: false,
      failed: false,
      uploadedByName: photo.uploadedByName,
      takenAt: photo.takenAt as string | null,
      createdAt: photo.createdAt as string | null,
    })),
    ...uploadedForOrder.map((entry) => ({
      clientId: entry.clientId,
      thumbSrc: localSrcs[entry.clientId]?.thumb ?? '',
      fullSrc: localSrcs[entry.clientId]?.full ?? '',
      pending: false,
      failed: false,
      uploadedByName: null,
      takenAt: entry.takenAtIso,
      createdAt: entry.enqueuedAtIso,
    })),
    ...queuedForOrder.map((entry) => ({
      clientId: entry.clientId,
      thumbSrc: localSrcs[entry.clientId]?.thumb ?? '',
      fullSrc: localSrcs[entry.clientId]?.full ?? '',
      pending: true,
      failed: entry.state === 'failed',
      uploadedByName: null,
      takenAt: entry.takenAtIso,
      createdAt: entry.enqueuedAtIso,
    })),
  ]

  return {
    photos,
    fetching: result.fetching,
    // A failed fetch must never render as "no photos yet"
    error: result.error !== undefined,
    refetch: () => reexecute({ requestPolicy: 'network-only' }),
  }
}
